import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Combobox, DropdownSelect, FormField, FormHelp, Label, VisuallyHidden } from '@zillow/constellation-10';
import { IsVisible } from '@zg-rentals/react-use';
import usePropertyFilter from '../../hooks/usePropertyFilter';
import useSelectedListingOptions from '../../hooks/useSelectedListingOptions';
import StringConstants from '../../constants/StringConstants';
import { SkeletonListItem } from '../LeadManagementPropertySelector/Skeleton';

type LeadManagementListingsComboboxProps = {
  selectedListingAliases: Array<string>;
  onChange: (listingAliases: Array<string>) => void;
  fullWidth?: boolean;
};

const NO_OP = () => {};

function getComboboxContainerStyles(fullWidth: boolean, isOpen: boolean) {
  if (fullWidth) {
    return { width: '100%' };
  }
  if (isOpen) {
    return { flex: 1, minWidth: 0 };
  }
  return { width: '100%', maxWidth: '480px' };
}

export default function LeadManagementListingsCombobox({
  selectedListingAliases,
  onChange,
  fullWidth = false,
}: LeadManagementListingsComboboxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const {
    errorMessage,
    hasMoreToPaginate,
    handleScroll,
    isLoadingSearchResults,
    isSearchInFlight,
    propertyListings,
    searchProperties,
    searchText,
  } = usePropertyFilter({
    updatePathWithListingAlias: NO_OP,
  });

  // Fires on select/remove/clear — all of which empty the combobox input programmatically (no onInput),
  // so reset the search too, keeping the list in sync: an empty input shows the full paginated list.
  const handleChange = useCallback(
    (newValue: Array<string>) => {
      onChange(newValue);
      searchProperties('');
    },
    [onChange, searchProperties],
  );

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
  }, []);

  const options = useMemo(
    () =>
      propertyListings.map((listing) => {
        const isCommunityListing = !!listing.meta;
        return {
          ...listing,
          label: isCommunityListing ? listing.label : (listing.streetAddress ?? listing.label),
          meta: isCommunityListing ? listing.meta : (listing.cityStateZip ?? ''),
        };
      }),
    [propertyListings],
  );

  // Cache every option we've shown so a selected listing keeps its chip even after a search narrows
  // the backend results to a set that no longer includes it.
  const knownOptionsRef = useRef(new Map<string, (typeof options)[number]>());
  useEffect(() => {
    for (const option of options) {
      knownOptionsRef.current.set(option.value, option);
    }
  }, [options]);

  // Selected aliases not in the current results still need a chip. Ones we've shown before come from
  // the cache; ones we've never loaded (e.g. a deep-linked alias) are resolved via getAddressDetails.
  const missingSelectedAliases = useMemo(() => {
    const loadedValues = new Set(options.map(({ value }) => value));
    return selectedListingAliases.filter((alias) => !loadedValues.has(alias));
  }, [options, selectedListingAliases]);
  const aliasesToFetch = useMemo(
    () => missingSelectedAliases.filter((alias) => !knownOptionsRef.current.has(alias)),
    [missingSelectedAliases],
  );
  const fetchedSelectedOptions = useSelectedListingOptions(aliasesToFetch);

  // Merge the missing selected listings back in so their chips render. The combobox's own input
  // filter keeps them out of the visible results while the user is searching.
  const optionsWithSelections = useMemo(() => {
    if (missingSelectedAliases.length === 0) return options;
    const fetchedByValue = new Map(fetchedSelectedOptions.map((option) => [option.value, option]));
    const restoredOptions = missingSelectedAliases
      .map((alias) => knownOptionsRef.current.get(alias) ?? fetchedByValue.get(alias))
      .filter((option) => option !== undefined);
    return restoredOptions.length > 0 ? [...options, ...restoredOptions] : options;
  }, [options, missingSelectedAliases, fetchedSelectedOptions]);

  // Last option shown for the current input, so the skeleton/pagination sentinel sits below the visible results.
  const lastVisibleOptionValue = useMemo(() => {
    const query = searchText.toLowerCase();
    return options
      .filter(({ label, meta }) => label.toLowerCase().includes(query) || (meta ?? '').toLowerCase().includes(query))
      .at(-1)?.value;
  }, [options, searchText]);

  if (isLoadingSearchResults && propertyListings.length === 0) {
    return (
      <FormField
        label={
          <Label>
            <VisuallyHidden>Listings</VisuallyHidden>
          </Label>
        }
        control={<DropdownSelect disabled options={[]} placeholder={StringConstants.LISTINGS_COMBOBOX_LOADING} />}
      />
    );
  }

  return (
    <Box css={getComboboxContainerStyles(fullWidth, isOpen)}>
      <FormField
        data-testid="lead-management-listings-combobox"
        label={
          <Label>
            <VisuallyHidden>Listings</VisuallyHidden>
          </Label>
        }
        error={!!errorMessage}
        description={errorMessage ? <FormHelp>{errorMessage}</FormHelp> : undefined}
        control={
          <Combobox
            size="md"
            options={optionsWithSelections}
            value={selectedListingAliases}
            onChange={handleChange}
            onOpenChange={handleOpenChange}
            placeholder={StringConstants.LISTINGS_COMBOBOX_PLACEHOLDER}
            appearance="select"
            autoCompleteBehavior="manual"
            showLabelForValue
            showOptionControl
            limitChipsShown={1}
            css={{ boxShadowColor: 'border.subtle' }}
            // onInput feeds the debounced server search; it runs alongside the combobox's own onChange so local
            // filtering stays instant. Drop ref: its LegacyRef type conflicts with Combobox.Input's Ref (unused at runtime).
            renderInput={({ ref: _ref, ...inputProps }) => (
              <Combobox.Input {...inputProps} onInput={(event) => searchProperties(event.currentTarget.value)} />
            )}
            // Skeleton instead of "No matches found" while searching; only fires when no local match, so local results still show.
            renderEmptyState={({ children }) =>
              isSearchInFlight ? <SkeletonListItem /> : <Combobox.EmptyState>{children}</Combobox.EmptyState>
            }
            renderOption={({
              option,
              highlightPattern,
              key,
              ref,
              renderOptionLabel,
              renderOptionMeta,
              renderOptionMedia,
              ...rest
            }) => {
              if (typeof option !== 'object') return null;

              const { value, label, meta } = option;
              const isLastItemInList = value === lastVisibleOptionValue;

              return (
                <Fragment key={key}>
                  <Combobox.Option option={option} ref={typeof ref === 'string' ? undefined : ref} {...rest}>
                    {renderOptionMedia?.({ children: option.icon })}
                    {renderOptionLabel?.({ children: label, highlightPattern })}
                    {meta && renderOptionMeta?.({ children: meta, highlightPattern })}
                  </Combobox.Option>

                  {isLastItemInList && hasMoreToPaginate && (
                    <IsVisible onChange={handleScroll} options={{ rootMargin: '0px 0px 200px 0px' }}>
                      {({ ref }) => <SkeletonListItem ref={ref} />}
                    </IsVisible>
                  )}

                  {/* Skeleton below local results while the backend fetches more. */}
                  {isLastItemInList && isSearchInFlight && !hasMoreToPaginate && <SkeletonListItem />}
                </Fragment>
              );
            }}
          />
        }
      />
    </Box>
  );
}
